import { useState } from 'react';
import axios from 'axios';
import TagManager from 'react-gtm-module';

const useEstimate = (formState, responseState, setResponseState, setDataLoaded, hardware, updateHardware) => {
  const [submitting, setSubmitting] = useState(false);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [satelliteImageUrl, setSatelliteImageUrl] = useState('');
  const [customerErrMesg, setCustomerErrMesg] = useState('');

  /**
   * What this wait is actually spent on, in order.
   *
   * These used to be jokes — "Watt's up Beep Boop..." — which read as filler
   * and left the customer with no idea whether anything was happening. The list
   * below tracks the real work `getEstimate` does: create the record, size the
   * system from the bill through PVWatts, pull the roof imagery, then send the
   * one-time code. It rotates on a timer rather than following the steps, so
   * each line has to be true for the whole wait, not just its own moment.
   */
  const loadingTexts = [
    'Setting up your account...',
    'Checking your local utility rates...',
    'Sizing a system for your bill...',
    'Finding your roof...',
    'Sending your one-time code...',
  ];

  const getEstimate = async () => {
    setSubmitting(true);
    try {
      // Display loading text
      setInterval(() => {
        setLoadingTextIndex((prevIndex) => (prevIndex + 1) % loadingTexts.length);
      }, 1600);

      const body = {
        name: formState.name,
        email: formState.email,
        phone: formState.phone,
        address: formState.address,
        customerName: formState.name,
        zip: formState.zip,
        aveBill: formState.electricBill,
        trades: hardware.solutions
      };

      const response = await fetch(process.env.REACT_APP_API_URL + '/estimate', {
          method: 'POST',
          headers: {
          'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
      });

      if (!response.ok) {
        let serverMessage = '';
        try {
          const errorBody = await response.json();
          serverMessage = errorBody?.message || errorBody?.error || '';
        } catch (parseError) {
          // Error body was not JSON — fall back to the status code below.
        }
        throw new Error(
          serverMessage || `We couldn't build your estimate right now. Please try again.`
        );
      }

      const result = await response.json();

      console.log("Estimate result", result);
      console.log("Estimate size", result.estimate_size);

      // A malformed/empty body used to produce estimateSize: NaN, which silently
      // dropped the whole solar path further down the flow instead of failing here.
      const estimateSizeKw = Number(result.estimate_size);
      if (!Number.isFinite(estimateSizeKw)) {
        throw new Error(`We couldn't build your estimate right now. Please try again.`);
      }

      // API call to get estimate data
      // const url = `${process.env.REACT_APP_API_URL}/estimate?zip=${formState.zip}&aveBill=${formState.electricBill}&address=${formState.address}&trades=${hardware.solutions}`;
      // const result = await axios.get(url);

      // Update response state with the result
      const updatedResponseState = { ...responseState,
        estimateSize: (estimateSizeKw * 1000),
        solarProductionMonthly: result.solarProductionMonthly
      };

      setResponseState(updatedResponseState);

      // Fetch satellite image. This is decorative — the estimate itself has
      // already succeeded, so an imagery failure must never fail the estimate
      // (and, now that the OTP gate honours this function's result, must never
      // block the customer from progressing).
      try {
        const image_url = `${process.env.REACT_APP_API_URL}/service/getHomeSatelliteImage?address=${encodeURIComponent(formState.address)}`;
        const imageResponse = await axios.get(image_url, { responseType: 'blob' });
        const imageBlob = imageResponse.data;
        const imageUrl = URL.createObjectURL(imageBlob);
        setSatelliteImageUrl(imageUrl);
        updateHardware('satelliteImage', imageUrl);
        updateHardware('designImage', imageUrl);
      } catch (imageError) {
        console.error("Satellite image unavailable", imageError.message);
      }

      // Push data to Tag Manager
      TagManager.dataLayer({
        gtmId: "GTM-MD88S8R",
        dataLayer: {
          event: 'locate_next_clicked_success'
        }
      });

      setDataLoaded(true);
      return true;
    } catch (error) {
      console.error("Error encountered", error.message);
      setCustomerErrMesg(error.message);
      TagManager.dataLayer({
        gtmId: "GTM-MD88S8R",
        dataLayer: {
          event: 'locate_next_clicked_estimate_error'
        }
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return { getEstimate, submitting, setSubmitting, satelliteImageUrl, loadingTextIndex, loadingTexts, customerErrMesg, setCustomerErrMesg };
};

export default useEstimate;
